import { PriceUtils } from '@wix/bookings-uou-mappers';
import { AddonSelector } from '@wix/bookings-viewer-ui';
import { useTranslation } from '@wix/yoshi-flow-editor';
import React from 'react';
import { Text, TextPriority } from 'wix-ui-tpa/cssVars';
import { SelectedAddons, SlotAddOn } from '../../../../../../utils/state/types';
import { useFormComponentContext } from '../../../../Hooks/useFormComponentContext';
import { classes } from '../../AddonsDetails.st.css';
import { AddonsDetailsDataHooks } from '../../datahooks';

// Pre-AddOnsWithImages behavior, kept byte-for-byte so the experiment's
// off-arm matches production exactly. See Addons/Group for the new UI.
export const GroupAddonsLegacy = ({
  name,
  maxQuantity,
  addOns,
  selectedAddons,
  onAddonSelect,
  onAddonQuantityChange,
  prompt,
  groupId,
}: {
  name: string;
  maxQuantity?: number;
  addOns?: SlotAddOn[];
  selectedAddons: SelectedAddons;
  onAddonSelect: (addonId: string, isSelected: boolean) => void;
  onAddonQuantityChange: (addonId: string, quantity: number) => void;
  prompt?: string;
  groupId: string;
}) => {
  const { t } = useTranslation();
  const { dateRegionalSettingsLocale } = useFormComponentContext();

  const groupSelectedAddons = selectedAddons[groupId] || {};
  const totalSelectedAddonsCount = Object.keys(selectedAddons).reduce(
    (count, gId) => count + Object.keys(selectedAddons[gId]).length,
    0,
  );

  return (
    <>
      <Text
        className={classes.groupName}
        data-hook={AddonsDetailsDataHooks.GROUP_NAME}
      >
        {name}
      </Text>
      {prompt && (
        <Text
          className={classes.groupSubTitle}
          data-hook={AddonsDetailsDataHooks.GROUP_PROMPT}
        >
          {prompt}
        </Text>
      )}
      {maxQuantity && (
        <Text
          priority={TextPriority.secondary}
          className={classes.groupLimitation}
        >
          {t('app.bookings-form.add-ons.group.max-limit', { maxQuantity })}
        </Text>
      )}
      <div className={classes.addonsContainer}>
        {addOns?.map((addOn, addonIndex) => {
          const selectedAddon = groupSelectedAddons[addOn.id!];
          return (
            <AddonSelector
              key={addOn.id}
              name={addOn.name!}
              price={
                addOn.price && addOn.price !== '0'
                  ? t('app.bookings-form.add-ons.plus.price', {
                      price: PriceUtils.getFormattedCurrency({
                        price: Number(addOn.price),
                        currency: addOn.currency!,
                        locale: dateRegionalSettingsLocale,
                        currencyDisplay: 'symbol',
                      }),
                    })
                  : ''
              }
              hasQuantity={selectedAddon && (addOn.maxQuantity ?? 1) > 1}
              maxQuantity={addOn.maxQuantity ?? undefined}
              quantity={selectedAddon?.quantity}
              onChange={(isSelected: boolean) =>
                onAddonSelect(addOn.id!, isSelected)
              }
              onCounterChange={(quantity: number) =>
                onAddonQuantityChange(addOn.id!, quantity)
              }
              checkboxProps={{
                checked: !!selectedAddon,
                disabled:
                  !selectedAddon && totalSelectedAddonsCount === maxQuantity,
              }}
            />
          );
        })}
      </div>
    </>
  );
};
